import { useRef } from 'react';
import { Repeat, Search, X } from 'lucide-react';
import { swapOptions } from './swaps';
import { daysBetweenKeys, findPreviousSession, formatSetShape } from './workout';
import { FONT_BODY, FONT_MONO } from './theme';
import { Button } from './ui';

/* ────────────────────────────────────────────────────────────────────────── */
/*  "Machine taken" swap sheet                                                */
/* ────────────────────────────────────────────────────────────────────────── */

const NUDGE_KEY = 'xceller_fit_swap_nudge'; // { 'from→to': dismissed date }, device-local
const NUDGE_SNOOZE_DAYS = 30;

const nudgeKey = (from, to) => `${from}→${to}`;
function readNudgeDismissals() {
  try {
    const v = JSON.parse(localStorage.getItem(NUDGE_KEY) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}

/* "Not now" hides the question for 30 days. */
export function nudgeSnoozed(from, to, today) {
  const at = readNudgeDismissals()[nudgeKey(from, to)];
  const ago = typeof at === 'string' ? daysBetweenKeys(at, today) : null;
  return ago !== null && ago >= 0 && ago < NUDGE_SNOOZE_DAYS;
}
export function snoozeNudge(from, to, today) {
  try {
    localStorage.setItem(NUDGE_KEY, JSON.stringify({ ...readNudgeDismissals(), [nudgeKey(from, to)]: today }));
  } catch {}
}

/* On an exercise card: Mo swaps this slot to the same exercise so often that
 * it may as well be the plan exercise. */
export function SwapNudge({ t, name, recent, onAccept, onDismiss }) {
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, background: t.accentSoft,
      border: `1px solid ${t.accentBorder}`, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Repeat size={14} color={t.accent} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.text, lineHeight: 1.35 }}>
            Make {name} the plan exercise?
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
            You swapped to it {recent}× in the last 30 days.
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button t={t} variant="primary" size="sm" onClick={onAccept} style={{ height: 40, flex: 1, justifyContent: 'center' }}>
          Make it the plan
        </Button>
        <Button t={t} variant="ghost" size="sm" onClick={onDismiss} style={{ height: 40, justifyContent: 'center' }}>
          Not now
        </Button>
      </div>
    </div>
  );
}

function OptionRow({ t, name, last, badge, onPick }) {
  return (
    <button onClick={onPick} style={{
      width: '100%', minHeight: 52, padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 12,
      background: 'transparent', border: 'none', borderTop: `1px solid ${t.divider}`, cursor: 'pointer',
      textAlign: 'left', fontFamily: FONT_BODY, touchAction: 'manipulation',
    }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </span>
        <span style={{ display: 'block', fontSize: 11, color: t.textMuted, fontFamily: FONT_MONO, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {last ? `last ${last}` : 'not logged yet'}
        </span>
      </span>
      {badge && (
        <span style={{
          flexShrink: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
          color: t.textFaint, fontFamily: FONT_MONO,
        }}>{badge}</span>
      )}
    </button>
  );
}

function SectionTitle({ t, children }) {
  return (
    <div style={{ padding: '12px 18px 6px', fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
      {children}
    </div>
  );
}

/**
 * One-tap substitutes for a plan exercise, no keyboard: Mo's usual swaps
 * (plan alternatives first), same-muscle alternatives, then "Search all…"
 * for the full picker.
 * @param {{id: string, name: string, originalName: string, planExercise: object}} ex
 *   the Today card (name = what is done now)
 * @param {string[]} todayNames the day's other exercises (not suggested)
 */
export function SwapSheet({ t, state, dateKey, ex, todayNames, onSwap, onSearch, onClose }) {
  // A tap that opened the sheet must not also close it through the backdrop.
  const openedAt = useRef(Date.now());
  const dismiss = () => { if (Date.now() - openedAt.current > 350) onClose(); };
  const { usual, sameMuscle } = swapOptions(state, {
    originalName: ex.originalName, currentName: ex.name, alts: ex.planExercise?.alts, todayNames,
  }, dateKey);
  const last = name => {
    const prev = findPreviousSession(state.logs, dateKey, name);
    return prev ? formatSetShape(prev.sets, 2) : '';
  };
  const pick = name => { onSwap(name); onClose(); };

  return (
    <div onClick={dismiss} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div role="dialog" aria-label={`Swap ${ex.name}`} onClick={e => e.stopPropagation()} style={{
        background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560,
        maxHeight: '75vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)',
        paddingBottom: 'env(safe-area-inset-bottom)', fontFamily: FONT_BODY,
      }}>
        <div style={{ padding: '10px 8px 8px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>Swap exercise</div>
            <div style={{ fontSize: 12, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ex.name}{ex.name !== ex.originalName ? ` · plan: ${ex.originalName}` : ''}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            width: 44, height: 44, flexShrink: 0, border: 'none', background: 'transparent', color: t.textFaint,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {usual.length > 0 && (
            <>
              <SectionTitle t={t}>Your usual swaps</SectionTitle>
              {usual.map(o => (
                <OptionRow key={o.name} t={t} name={o.name} last={last(o.name)}
                  badge={o.plan ? (o.count ? `plan · ${o.count}×` : 'plan alt') : `used ${o.count}×`}
                  onPick={() => pick(o.name)} />
              ))}
            </>
          )}
          {sameMuscle.length > 0 && (
            <>
              <SectionTitle t={t}>Same muscle</SectionTitle>
              {sameMuscle.map(o => (
                <OptionRow key={o.name} t={t} name={o.name} last={last(o.name)} badge={o.equipment}
                  onPick={() => pick(o.name)} />
              ))}
            </>
          )}
          {!usual.length && !sameMuscle.length && (
            <div style={{ padding: '16px 18px', fontSize: 13, color: t.textMuted }}>
              No suggestions for this exercise yet.
            </div>
          )}
        </div>

        <div style={{ padding: '10px 18px 14px', borderTop: `1px solid ${t.divider}` }}>
          <Button t={t} onClick={onSearch} style={{ width: '100%', height: 44, justifyContent: 'center' }}>
            <Search size={14} /> Search all…
          </Button>
        </div>
      </div>
    </div>
  );
}
